if (!customElements.get("nmn-video-library")) {
  customElements.define(
    "nmn-video-library",
    class extends HTMLElement {
      connectedCallback() {
        this.closePlayer();
        this.controller?.abort();
        this.observer?.disconnect();
        this.controller = new AbortController();
        this.grid = this.querySelector("[data-video-grid]");
        this.controls = this.querySelector("[data-video-controls]");
        this.button = this.querySelector("[data-video-more]");
        if (!this.grid || !this.controls || !this.button) return;

        this.setupPlayer();

        const deferred = this.dataset.defer === "true" && !this.hasAttribute("data-editor");
        this.grid.hidden = deferred;
        this.controls.hidden = !deferred;
        this.button.setAttribute("aria-expanded", String(!deferred));
        this.button.addEventListener("click", () => this.reveal(true), {
          signal: this.controller.signal,
        });
        if (!deferred || !("IntersectionObserver" in window)) return;

        // One batch at a time, even when several collapsed batches share the viewport.
        this.observer = new IntersectionObserver(() => this.revealNearby(), {
          rootMargin: "200px 0px",
        });
        this.observer.observe(this.controls);
        document.addEventListener("nmn:video-batch-revealed", () => this.revealNearby(), {
          signal: this.controller.signal,
        });
      }

      revealNearby() {
        if (!this.grid.hidden || this.contains(document.activeElement)) return;
        const previousPending = Array.from(document.querySelectorAll("nmn-video-library"))
          .some((batch) =>
            batch !== this &&
            (batch.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING) &&
            batch.querySelector("[data-video-grid]")?.hidden,
          );
        const bounds = this.controls.getBoundingClientRect();
        if (!previousPending && bounds.top < window.innerHeight + 200 && bounds.bottom > 0) {
          this.reveal(false);
        }
      }

      reveal(moveFocus) {
        if (!this.grid.hidden) return;
        this.grid.hidden = false;
        this.button.setAttribute("aria-expanded", "true");
        if (moveFocus) {
          const firstLink = this.grid.querySelector("button[data-video-open], a");
          if (firstLink) firstLink.focus({ preventScroll: true });
          else {
            this.grid.tabIndex = -1;
            this.grid.focus({ preventScroll: true });
          }
        }
        this.controls.hidden = true;
        this.observer?.disconnect();
        document.dispatchEvent(new CustomEvent("nmn:video-batch-revealed"));
      }

      setupPlayer() {
        this.dialog = this.querySelector("[data-video-dialog]");
        this.player = this.querySelector("[data-video-player]");
        this.playerTitle = this.querySelector("[data-video-dialog-title]");
        if (!this.dialog?.showModal || !this.player || !this.playerTitle) return;
        const signal = this.controller.signal;

        // Preserve real provider links until enhancement succeeds.
        this.querySelectorAll("a.nmn-video-library__card-link").forEach((link) => {
          const source = this.embedSource(link.href);
          if (!source) return;
          const button = document.createElement("button");
          button.type = "button";
          button.className = link.className;
          button.dataset.videoOpen = source;
          button.setAttribute("aria-labelledby", link.getAttribute("aria-labelledby"));
          button.setAttribute("aria-haspopup", "dialog");
          button.setAttribute("aria-controls", this.dialog.id);
          button.append(...link.childNodes);
          link.replaceWith(button);
        });
        this.addEventListener("click", (event) => {
          const trigger = event.target.closest("[data-video-open]");
          if (trigger && this.contains(trigger)) this.openPlayer(trigger);
        }, { signal });
        this.querySelector("[data-video-close]")?.addEventListener("click", () => this.closePlayer(), { signal });
        this.dialog.addEventListener("click", (event) => {
          if (event.target !== this.dialog) return;
          const bounds = this.dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.closePlayer();
        }, { signal });
        this.dialog.addEventListener("close", () => {
          this.player.replaceChildren();
          if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
          this.opener = null;
        }, { signal });
        document.addEventListener("nmn:video-player-open", (event) => {
          if (event.detail !== this) this.closePlayer();
        }, { signal });
      }

      embedSource(value) {
        try {
          const url = new URL(value);
          if (url.protocol !== "https:" && url.protocol !== "http:") return null;
          const host = url.hostname.replace(/^www\./, "");
          let id;
          if (host === "youtu.be") id = url.pathname.split("/")[1];
          else if (["youtube.com", "m.youtube.com", "youtube-nocookie.com"].includes(host)) {
            id = url.searchParams.get("v") || url.pathname.match(/^\/(?:embed|shorts|live)\/([^/]+)/)?.[1];
          }
          if (id && /^[\w-]{11}$/.test(id)) {
            const embed = new URL(`https://www.youtube.com/embed/${id}`);
            embed.searchParams.set("autoplay", "1");
            embed.searchParams.set("enablejsapi", "1");
            const time = url.searchParams.get("start") || url.searchParams.get("t") || "";
            const match = time.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
            const seconds = /^\d+$/.test(time) ? Number(time) : match ? Number(match[1] || 0) * 3600 + Number(match[2] || 0) * 60 + Number(match[3] || 0) : 0;
            if (seconds > 0) embed.searchParams.set("start", String(seconds));
            return embed.href;
          }
          if (host === "vimeo.com" || host === "player.vimeo.com") {
            const video = url.pathname.match(/^\/(?:video\/)?(\d+)(?:\/([a-zA-Z0-9]+))?\/?$/);
            if (!video) return null;
            const embed = new URL(`https://player.vimeo.com/video/${video[1]}`);
            embed.searchParams.set("autoplay", "1");
            const hash = url.searchParams.get("h") || video[2];
            if (hash) embed.searchParams.set("h", hash);
            return embed.href;
          }
        } catch {
          return null;
        }
        return null;
      }

      openPlayer(trigger) {
        document.dispatchEvent(new CustomEvent("nmn:video-player-open", { detail: this }));
        window.pauseAllMedia?.();
        this.opener = trigger;
        const title = trigger.querySelector("h2")?.textContent || "";
        this.playerTitle.textContent = title;
        const iframe = document.createElement("iframe");
        iframe.src = trigger.dataset.videoOpen;
        iframe.title = title;
        iframe.allow = "autoplay; encrypted-media; picture-in-picture; fullscreen";
        iframe.allowFullscreen = true;
        iframe.referrerPolicy = "strict-origin-when-cross-origin";
        iframe.className = iframe.src.includes("youtube.com") ? "js-youtube" : "js-vimeo";
        this.player.replaceChildren(iframe);
        this.dialog.showModal();
      }

      closePlayer() {
        if (this.dialog?.open) this.dialog.close();
        this.player?.replaceChildren();
      }

      disconnectedCallback() {
        this.closePlayer();
        this.controller?.abort();
        this.observer?.disconnect();
      }
    },
  );
}
